<template>
    <PrimeAvatar size="md">
        <img
            v-if="avatarUrl && !failed"
            :src="avatarUrl"
            alt=""
            draggable="false"
            class="person-avatar__image"
            @error="failed = true"
        >
        <template v-else>{{ initials }}</template>
    </PrimeAvatar>
</template>

<script setup lang="ts">
/**
 * One person's avatar. `avatarUrl` is the server-derived SparkVault Identity
 * address (Person::$avatar_url); that host answers 404 for anyone without a
 * public avatar — a normal answer, not an error — so a failed load falls back
 * to the monogram, same as an absent URL.
 */
import { computed, ref, toRef, watch } from 'vue';
import { initials as nameInitials } from '@privacy-bee/format/text';

const props = defineProps({
    name: {
        type: String,
        required: false,
        default: 'Anonymous',
    },
    avatarUrl: {
        type: String as () => string | null,
        required: false,
        default: null,
    },
});

const failed = ref(false);
// A row is recycled across people as a list re-renders, so the error flag has
// to follow the URL rather than the component instance.
watch(toRef(props, 'avatarUrl'), () => {
    failed.value = false;
});

const initials = computed(() => nameInitials(props.name));
</script>

<style lang="scss" scoped>
.person-avatar__image {
    @apply w-full h-full rounded-full object-cover select-none;
}
</style>
